(function () {
  const SELECTORS = {
    bundles: ".kaching-bundles",
    firstWrapper: ".kaching-bundles__bar-wrapper",
    subscriptionCheckbox: ".kaching-bundles__subscriptions__checkbox",
    productForm: 'form[data-type="add-to-cart-form"]',
    submitButton: 'button[type="submit"][name="add"]',
    origprice: ".kaching-bundles__bar-price",
    comprice: ".kaching-bundles__bar-full-price",
    variantsScript: "[data-qty-btn-variants]",
    variantPickerScript:
      'variant-radios script[type="application/json"], variant-selects script[type="application/json"]',
  };

  const STATE = {
    quantity: 1,
    allVariants: [],
    pack: {},
    sellingPlanGroups: [],
    currency:
      document.documentElement.lang && window.Shopify?.currency?.active
        ? window.Shopify.currency.active
        : "USD",
    isProcessing: false,
    variantsReady: false,
  };

  let moneyFormatter = null;
  let setupDone = false;
  let bundlesObserver = null;
  let bundlesSyncTimer = null;
  let priceSyncGuard = false;
  let priceObserver = null;
  let toggleSettleTimer = null;
  let toggleSettleRaf = null;

  function clampQuantity(value) {
    return Math.max(1, Number.parseInt(value, 10) || 1);
  }

  function loadStoredQuantity() {
    const stored = window.sessionStorage.getItem("qty-btn-quantity");
    STATE.quantity = clampQuantity(stored);
  }

  function persistQuantity() {
    window.sessionStorage.setItem("qty-btn-quantity", String(STATE.quantity));
  }

  function getMoneyFormatter() {
    if (!moneyFormatter) {
      moneyFormatter = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: STATE.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return moneyFormatter;
  }

  function formatMoney(cents) {
    return getMoneyFormatter().format(Number(cents) / 100);
  }

  function resolveCurrency() {
    return window.Shopify?.currency?.active || "USD";
  }

  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function parseDeliveryMonths(text) {
    if (!text) return null;
    const lower = String(text).toLowerCase();
    if (lower.includes("every month") && !/every \d+ month/.test(lower)) return 1;

    const match = lower.match(/every (\d+) month/);
    return match ? Number.parseInt(match[1], 10) : null;
  }

  function mapSellingPlanGroupsToPackSizes(groups) {
    const planByPackSize = {};

    (groups || []).forEach((group) => {
      const groupMonths = parseDeliveryMonths(group.name);

      (group.selling_plans || []).forEach((plan) => {
        const months =
          parseDeliveryMonths(plan.name) ||
          parseDeliveryMonths(plan.options?.[0]?.value) ||
          groupMonths;
        if (!months) return;

        const price = plan.price_adjustments?.[0]?.value;
        if (price == null) return;

        planByPackSize[months] = {
          selling_plan_id: plan.id,
          price: Number(price),
          per_delivery_price: Number(price),
        };
      });
    });

    return planByPackSize;
  }

  function applySellingPlanGroupsToVariants(groups) {
    STATE.sellingPlanGroups = groups || [];
  }

  function getPlanByPackSize(packSize) {
    const planByPackSize = mapSellingPlanGroupsToPackSizes(STATE.sellingPlanGroups);
    return packSize ? planByPackSize[packSize] || null : null;
  }

  function mergeVariantFromApi(apiVariant) {
    let variant = STATE.allVariants.find((entry) => entry.id === apiVariant.id);

    if (!variant) {
      variant = apiVariant;
      STATE.allVariants.push(variant);
    }

    variant.price = apiVariant.price;
    variant.title = apiVariant.title || variant.title;
    variant.option1 = apiVariant.option1 ?? variant.option1;

    if (apiVariant.selling_plan_allocations?.length) {
      variant.selling_plan_allocations = apiVariant.selling_plan_allocations;
    } else {
      variant.selling_plan_allocations = [];
    }

    return variant;
  }

  function getSellingPlanGroupsScriptContent() {
    const script = document.querySelector("[data-qty-btn-selling-plan-groups]");
    return script ? script.textContent : null;
  }

  function enrichVariantsFromKaching() {
    const script = document.querySelector('script.kaching-bundles-product[data-main="true"]');
    if (!script) return;

    try {
      const kachingProduct = JSON.parse(script.textContent);
      if (!kachingProduct?.variants) return;

      kachingProduct.variants.forEach((kachingVariant) => {
        const variant = STATE.allVariants.find((entry) => entry.id === kachingVariant.id);
        if (!variant || !kachingVariant.sellingPlans?.length) return;
        if (variant.selling_plan_allocations?.length) return;

        variant.selling_plan_allocations = kachingVariant.sellingPlans.map((plan) => ({
          selling_plan_id: plan.id,
          price: plan.price,
          per_delivery_price: plan.price,
        }));
      });
    } catch (error) {
      console.warn("[qty-btn] Could not merge Kaching selling plans:", error);
    }
  }

  async function refreshVariantPlansFromApi() {
    const handle = getProductHandle();
    if (!handle) return;

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const product = await response.json();
      (product.variants || []).forEach((apiVariant) => {
        mergeVariantFromApi(apiVariant);
      });

      applySellingPlanGroupsToVariants(product.selling_plan_groups || []);
    } catch (error) {
      console.warn("[qty-btn] Could not refresh variant plans from API:", error);
    }
  }

  function applyEmbeddedSellingPlanGroups() {
    const content = getSellingPlanGroupsScriptContent();
    if (!content) return;

    try {
      applySellingPlanGroupsToVariants(JSON.parse(content));
    } catch (error) {
      console.warn("[qty-btn] Could not parse embedded selling plan groups:", error);
    }
  }

  function getVariantSubscription(variant) {
    const allocation = variant.selling_plan_allocations?.[0];
    if (!allocation) return null;

    const sellingPlanId = Number(allocation.selling_plan_id);
    if (!sellingPlanId) return null;

    return {
      sellingPlanId,
      price: Number(allocation.price ?? allocation.per_delivery_price) || 0,
      source: "allocation",
    };
  }

  function getDecomposedParts() {
    return decompose(STATE.quantity).map((item) => ({
      ...item,
      subscription: getVariantSubscription(item.variant),
      packSize: packSizeFromVariant(item.variant),
    }));
  }

  function canUseDecomposedSubscription(parts) {
    return parts.length > 0 && parts.every((part) => part.subscription);
  }

  function getSingleBottleSubscriptionFallback() {
    const singleVariant = STATE.pack[1];
    if (!singleVariant) return null;

    const subscription = getVariantSubscription(singleVariant);
    if (subscription) {
      return {
        variant: singleVariant,
        subscription,
      };
    }

    return null;
  }

  function getSubscriptionPricing() {
    const parts = getDecomposedParts();

    if (canUseDecomposedSubscription(parts)) {
      return {
        mode: "decomposed",
        parts,
        displayTotal: parts.reduce(
          (total, part) => total + part.subscription.price * Number(part.qty),
          0,
        ),
        strikeTotal: parts.reduce(
          (total, part) => total + Number(part.variant.price) * Number(part.qty),
          0,
        ),
      };
    }

    const fallback = getSingleBottleSubscriptionFallback();
    if (fallback) {
      return {
        mode: "single-fallback",
        parts,
        displayTotal: fallback.subscription.price * STATE.quantity,
        strikeTotal: parts.reduce(
          (total, part) => total + Number(part.variant.price) * Number(part.qty),
          0,
        ),
      };
    }

    return {
      mode: "one-time",
      parts,
      displayTotal: parts.reduce(
        (total, part) => total + Number(part.variant.price) * Number(part.qty),
        0,
      ),
      strikeTotal: 0,
    };
  }

  function buildSubscriptionItems() {
    const parts = getDecomposedParts();

    if (canUseDecomposedSubscription(parts)) {
      return parts.map((part) => ({
        id: part.variant.id,
        quantity: part.qty,
        selling_plan: part.subscription.sellingPlanId,
      }));
    }

    const fallback = getSingleBottleSubscriptionFallback();
    if (fallback) {
      return [
        {
          id: fallback.variant.id,
          quantity: STATE.quantity,
          selling_plan: fallback.subscription.sellingPlanId,
        },
      ];
    }

    return parts.map((part) => ({
      id: part.variant.id,
      quantity: part.qty,
    }));
  }

  function getVariantsScriptContent() {
    const explicit = document.querySelector(SELECTORS.variantsScript);
    if (explicit) return explicit.textContent;

    const picker = document.querySelector(SELECTORS.variantPickerScript);
    if (picker) return picker.textContent;

    return null;
  }

  function packSizeFromVariant(variant) {
    const source = ((variant.option1 || variant.title) + "").toLowerCase().trim();
    if (/\bsingle\b/.test(source)) return 1;
    const match = source.match(/\d+/);
    return match ? Number.parseInt(match[0], 10) : null;
  }

  function buildPackMap() {
    STATE.pack = {};
    STATE.allVariants.forEach((variant) => {
      const packSize = packSizeFromVariant(variant);
      if (packSize !== null) STATE.pack[packSize] = variant;
    });

    if (!STATE.pack[1] || !STATE.pack[2] || !STATE.pack[3]) {
      console.warn(
        "[qty-btn] Could not map all pack sizes. Mapped:",
        Object.keys(STATE.pack).join(", "),
        "| Variant titles:",
        STATE.allVariants.map((variant) => variant.title).join(", "),
      );
    }
  }

  function bootstrapVariantAllocationsFromGroups() {
    const planByPackSize = mapSellingPlanGroupsToPackSizes(STATE.sellingPlanGroups);

    STATE.allVariants.forEach((variant) => {
      if (variant.selling_plan_allocations?.length) return;

      const packSize = packSizeFromVariant(variant);
      const plan = packSize ? planByPackSize[packSize] : null;
      if (!plan) return;

      variant.selling_plan_allocations = [
        {
          selling_plan_id: plan.selling_plan_id,
          price: plan.price,
          per_delivery_price: plan.per_delivery_price,
        },
      ];
    });
  }

  function bootstrapVariantsSync() {
    const content = getVariantsScriptContent();
    if (content) {
      try {
        STATE.allVariants = JSON.parse(content);
      } catch (error) {
        console.warn("[qty-btn] Could not parse embedded variants:", error);
        return false;
      }
    } else if (!STATE.allVariants.length) {
      return false;
    }

    applyEmbeddedSellingPlanGroups();
    bootstrapVariantAllocationsFromGroups();
    enrichVariantsFromKaching();
    buildPackMap();
    STATE.variantsReady = STATE.allVariants.length > 0;
    return STATE.variantsReady;
  }

  async function loadVariants() {
    bootstrapVariantsSync();

    const handle = getProductHandle();
    if (!handle) {
      if (!STATE.variantsReady) {
        console.warn("[qty-btn] Could not find product variants.");
      }
      return;
    }

    try {
      await refreshVariantPlansFromApi();
      applyEmbeddedSellingPlanGroups();
      enrichVariantsFromKaching();
      buildPackMap();
      STATE.variantsReady = STATE.allVariants.length > 0;
    } catch (error) {
      console.error("[qty-btn] Failed to refresh variants:", error);
    }
  }

  async function refreshSellingPlans() {
    if (!STATE.allVariants.length && !bootstrapVariantsSync()) {
      await loadVariants();
      return;
    }

    await refreshVariantPlansFromApi();
    applyEmbeddedSellingPlanGroups();
    enrichVariantsFromKaching();
    buildPackMap();
  }

  function markPricingReady() {
    const bundles = document.querySelector(SELECTORS.bundles);
    if (bundles) {
      bundles.classList.add("qty-btn--ready");
    }
  }

  function primeInitialPricingMode() {
    const bundles = document.querySelector(SELECTORS.bundles);
    if (!bundles) return;

    syncCompareVisibility(isSubscription());
  }

  // Greedy 3-bottle first. Remainder: 0→nothing, 1→single, 2→2-bottle.
  function decompose(totalBottles) {
    const parts = [];
    const threes = Math.floor(totalBottles / 3);
    const remainder = totalBottles % 3;

    if (threes > 0 && STATE.pack[3]) {
      parts.push({ variant: STATE.pack[3], qty: threes });
    }
    if (remainder === 1 && STATE.pack[1]) {
      parts.push({ variant: STATE.pack[1], qty: 1 });
    }
    if (remainder === 2 && STATE.pack[2]) {
      parts.push({ variant: STATE.pack[2], qty: 1 });
    }

    return parts;
  }

  function isSubscription() {
    const subscriptionToggle = document.querySelector(".subscriptionMine.bundleMineData");
    const oneTimeToggle = document.querySelector(".oneTimeMine.bundleMineData");

    if (subscriptionToggle?.classList.contains("active")) return true;
    if (
      oneTimeToggle?.classList.contains("active") &&
      !subscriptionToggle?.classList.contains("active")
    ) {
      return false;
    }

    const checkbox = document.querySelector(SELECTORS.subscriptionCheckbox);
    if (!checkbox) return false;

    return checkbox.querySelector("svg") !== null;
  }

  function getFirstWrapper(root) {
    return root.querySelector(SELECTORS.firstWrapper);
  }

  function getControl(root) {
    return root.querySelector("[data-qty-btn-root]");
  }

  function getValueInput(control) {
    return control.querySelector("[data-qty-btn-value]");
  }

  function getFirstBarPriceElements() {
    const bundles = document.querySelector(SELECTORS.bundles);
    if (!bundles) return {};

    const firstWrapper = getFirstWrapper(bundles);
    if (!firstWrapper) return {};

    const priceEl = firstWrapper.querySelector(SELECTORS.origprice);

    return {
      priceEl,
      strikeEl: firstWrapper.querySelector(SELECTORS.comprice),
      pricingContainer: priceEl?.closest(".kaching-bundles__bar-pricing") || null,
    };
  }

  function ensureStrikeElement(pricingContainer, strikeEl) {
    if (strikeEl || !pricingContainer) return strikeEl;

    strikeEl = document.createElement("div");
    strikeEl.className = "kaching-bundles__bar-full-price";
    strikeEl.setAttribute("data-a11y-label", "system.original_price");
    pricingContainer.appendChild(strikeEl);
    return strikeEl;
  }

  function syncCompareVisibility(useSubscription) {
    const bundles = document.querySelector(SELECTORS.bundles);
    if (!bundles) return;

    bundles.classList.toggle("qty-btn--subscription", useSubscription);
    bundles.classList.toggle("qty-btn--one-time", !useSubscription);

    bundles.querySelectorAll(SELECTORS.comprice).forEach((compareEl) => {
      if (useSubscription) {
        compareEl.removeAttribute("hidden");
        compareEl.style.removeProperty("display");
      } else {
        compareEl.setAttribute("hidden", "");
        compareEl.style.setProperty("display", "none", "important");
        compareEl.textContent = "";
      }
    });
  }

  function applyPriceDisplay(priceEl, strikeEl, pricingContainer, useSubscription, displayTotal, strikeTotal) {
    const nextPrice = formatMoney(displayTotal);

    if (priceEl.textContent !== nextPrice) {
      priceEl.textContent = nextPrice;
    }

    if (useSubscription) {
      const activeStrikeEl = ensureStrikeElement(pricingContainer, strikeEl);
      if (activeStrikeEl) {
        const nextStrike = formatMoney(strikeTotal);
        if (activeStrikeEl.textContent !== nextStrike) {
          activeStrikeEl.textContent = nextStrike;
        }
      }
    }
  }

  function syncPrices() {
    if (!STATE.variantsReady || priceSyncGuard) return;

    const { priceEl, strikeEl: existingStrikeEl, pricingContainer } =
      getFirstBarPriceElements();
    if (!priceEl) return;

    const useSubscription = isSubscription();
    const parts = decompose(STATE.quantity);
    let displayTotal = 0;
    let strikeTotal = parts.reduce(
      (total, part) => total + Number(part.variant.price) * Number(part.qty),
      0,
    );

    if (useSubscription) {
      const pricing = getSubscriptionPricing();
      displayTotal = pricing.displayTotal;
      strikeTotal = pricing.strikeTotal;
    } else {
      displayTotal = strikeTotal;
    }

    priceSyncGuard = true;
    try {
      syncCompareVisibility(useSubscription);
      applyPriceDisplay(
        priceEl,
        existingStrikeEl,
        pricingContainer,
        useSubscription,
        displayTotal,
        strikeTotal,
      );
      markPricingReady();
    } finally {
      priceSyncGuard = false;
    }
  }

  function runPriceSyncBurst() {
    syncPrices();

    if (toggleSettleRaf) {
      window.cancelAnimationFrame(toggleSettleRaf);
    }

    toggleSettleRaf = window.requestAnimationFrame(() => {
      syncPrices();
      window.requestAnimationFrame(() => syncPrices());
    });

    window.clearTimeout(toggleSettleTimer);
    toggleSettleTimer = window.setTimeout(() => {
      syncPrices();
      window.setTimeout(syncPrices, 100);
    }, 50);
  }

  function watchFirstBarPricing() {
    const { priceEl, strikeEl, pricingContainer } = getFirstBarPriceElements();
    if (!priceEl || priceEl.dataset.qtyBtnPriceObserved === "true") return;

    priceEl.dataset.qtyBtnPriceObserved = "true";

    if (priceObserver) {
      priceObserver.disconnect();
    }

    const targets = [priceEl, strikeEl, pricingContainer].filter(Boolean);
    priceObserver = new MutationObserver(() => {
      if (priceSyncGuard) return;
      syncPrices();
    });

    targets.forEach((target) => {
      priceObserver.observe(target, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    });
  }

  function syncFrequency() {
    const freqSpan = document.querySelector(".autoShipPlan span");
    if (!freqSpan) return;

    const months = Math.min(STATE.quantity, 3);
    freqSpan.textContent = months === 1 ? "Month" : `${months} Months`;
  }

  function syncValue(control) {
    const valueInput = getValueInput(control);
    if (!valueInput) return;

    valueInput.value = String(STATE.quantity);
    valueInput.setAttribute("aria-valuenow", String(STATE.quantity));

    const minusButton = control.querySelector('[data-step="-1"]');
    if (minusButton) {
      minusButton.disabled = STATE.quantity <= 1;
    }
  }

  function setQuantity(nextValue, control) {
    STATE.quantity = clampQuantity(nextValue);
    persistQuantity();
    syncValue(control);
    syncPrices();
    syncFrequency();
  }

  function onButtonClick(event) {
    const button = event.currentTarget;
    const control = button.closest("[data-qty-btn-root]");
    if (!control) return;

    const delta = Number.parseInt(button.getAttribute("data-step"), 10) || 0;
    setQuantity(STATE.quantity + delta, control);
  }

  function blockManualInput(event) {
    event.preventDefault();
    const control = event.currentTarget.closest("[data-qty-btn-root]");
    if (!control) return;

    if (event.type === "keydown") {
      if (event.key === "ArrowUp") setQuantity(STATE.quantity + 1, control);
      if (event.key === "ArrowDown") setQuantity(STATE.quantity - 1, control);
    }
  }

  function createControl() {
    const wrapper = document.createElement("div");
    wrapper.className = "buy-buttons-qty__quantity qty-btn";
    wrapper.setAttribute("data-qty-btn-root", "");
    wrapper.setAttribute("data-quantity-selector", "");

    wrapper.innerHTML = [
      '<button class="buy-buttons-qty__quantity-button buy-buttons-qty__quantity-button--muted qty-btn__button" data-step="-1" type="button" aria-label="Decrease quantity">',
      '<span aria-hidden="true">&minus;</span>',
      "</button>",
      '<input class="buy-buttons-qty__quantity-input qty-btn__value" data-qty-btn-value type="number" inputmode="numeric" pattern="[0-9]*" readonly aria-label="Bundle quantity" aria-live="polite" aria-valuemin="1" aria-valuenow="1" value="1">',
      '<button class="buy-buttons-qty__quantity-button qty-btn__button" data-step="1" type="button" aria-label="Increase quantity">',
      '<span aria-hidden="true">+</span>',
      "</button>",
    ].join("");

    wrapper.querySelectorAll(".qty-btn__button").forEach((button) => {
      button.addEventListener("click", onButtonClick);
    });

    const input = wrapper.querySelector("[data-qty-btn-value]");
    ["beforeinput", "input", "paste", "drop", "wheel"].forEach((eventName) => {
      input.addEventListener(eventName, blockManualInput);
    });
    input.addEventListener("keydown", blockManualInput);

    syncValue(wrapper);
    return wrapper;
  }

  function injectStyles() {
    if (document.getElementById("qty-btn-styles")) return;

    const style = document.createElement("style");
    style.id = "qty-btn-styles";
    style.textContent = [
      ".kaching-bundles__bar-wrapper:has(> .qty-btn){display:flex;align-items:center;justify-content:space-between;gap:1rem;flex-wrap:nowrap;}",
      ".kaching-bundles__bar-wrapper:has(> .qty-btn) > .kaching-bundles__bar-main{flex:1 1 auto;min-width:0;}",
      ".qty-btn.buy-buttons-qty__quantity{display: inline-flex;align-items: center;justify-content: space-between;gap: .6rem;flex: 0 0 auto;min-width: 0rem;padding: .6rem;border: .1rem solid #d9dcc8;border-radius: 999px;background: #f8f8f2;}",
      ".qty-btn .buy-buttons-qty__quantity-button{width:2.6rem;height:2.6rem;border:1px solid #3b453b;border-radius:999px;background:#fff;color:#2f2f2f;font-size:1.2rem;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;transition:border-color .2s ease, background-color .2s ease;}",
      ".qty-btn .buy-buttons-qty__quantity-button:hover{border-color:#c9b7aa;background:#faf8f5;}",
      ".qty-btn .buy-buttons-qty__quantity-button:disabled,.qty-btn .buy-buttons-qty__quantity-button--muted{opacity:.45;cursor:not-allowed;}",
      ".qty-btn .buy-buttons-qty__quantity-input{width:4rem;min-height:2.6rem;border:none;border-radius:0px;background:none;color:#2f2f2f;font-size:1.4rem;line-height:1.1;text-align:center;padding:0.2rem 0.4rem;pointer-events:none;}",
      ".qty-btn .buy-buttons-qty__quantity-button:focus-visible,.qty-btn .buy-buttons-qty__quantity-input:focus-visible{outline:2px solid #2f2f2f;outline-offset:2px;}",
      ".qty-btn .buy-buttons-qty__quantity-input::-webkit-outer-spin-button,.qty-btn .buy-buttons-qty__quantity-input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}",
      ".qty-btn .buy-buttons-qty__quantity-input[type=number]{-moz-appearance:textfield;}",
      ".kaching-bundles.qty-btn--one-time .kaching-bundles__bar-full-price{display:none!important;}",
      "html.qty-btn-page .kaching-bundles:not(.qty-btn--ready) .kaching-bundles__bar-wrapper:first-child .kaching-bundles__bar-pricing{visibility:hidden;}",
      ".kaching-bundles__bar-wrapper:has(> .qty-btn) .kaching-bundles__bar-pricing{min-height:1.6em;}",
      ".kaching-bundles__bar-wrapper:has(> .qty-btn) .kaching-bundles__bar-price,.kaching-bundles__bar-wrapper:has(> .qty-btn) .kaching-bundles__bar-full-price{transition:none!important;}",
      "@media screen and (max-width:749px){",
      ".kaching-bundles__bar-wrapper:has(> .qty-btn){gap:0.5rem;}",
      ".qty-btn.buy-buttons-qty__quantity{gap:0.4rem;}",
      ".qty-btn .buy-buttons-qty__quantity-button{width:2rem;height:2rem;font-size:1.1rem;}",
      ".qty-btn .buy-buttons-qty__quantity-input{width:3.4rem;min-height:2rem;font-size:1.2rem;padding:0.1rem 0.3rem;}",
      "}",
      "html.skip-cart-active cart-drawer,",
      "html.skip-cart-active cart-drawer.active,",
      "html.skip-cart-active #CartDrawer,",
      "html.skip-cart-active .cart-drawer,",
      "html.skip-cart-active kaching-cart,",
      "html.skip-cart-active [class*='kaching-cart'],",
      "html.skip-cart-active [id*='kaching-cart']{",
      "display:none!important;",
      "visibility:hidden!important;",
      "opacity:0!important;",
      "pointer-events:none!important;",
      "}",
    ].join("");

    document.head.appendChild(style);
  }

  function ensureControl() {
    const bundles = document.querySelector(SELECTORS.bundles);
    if (!bundles) return;

    const firstWrapper = getFirstWrapper(bundles);
    if (!firstWrapper) return;

    if (!getControl(firstWrapper)) {
      firstWrapper.appendChild(createControl());
    } else {
      syncValue(getControl(firstWrapper));
    }

    syncPrices();
    syncFrequency();
    watchFirstBarPricing();
  }

  function usesCustomSubscriptionToggle() {
    return Boolean(document.querySelector(".cyberButtonsMine"));
  }

  function ensureSubscriptionSelected() {
    if (usesCustomSubscriptionToggle()) {
      syncPrices();
      return;
    }

    if (window.sessionStorage.getItem("qty-btn-subscription-auto-clicked") === "true") {
      return;
    }

    const checkbox = document.querySelector(SELECTORS.subscriptionCheckbox);
    if (!checkbox || checkbox.dataset.qtyBtnAutoClickPending === "true") {
      return;
    }

    const checkboxCard = checkbox.closest('[role="checkbox"]');
    if (checkboxCard?.getAttribute("aria-checked") === "true") {
      window.sessionStorage.setItem("qty-btn-subscription-auto-clicked", "true");
      syncPrices();
      return;
    }

    const attempts = Number.parseInt(
      window.sessionStorage.getItem("qty-btn-subscription-auto-click-attempts") || "0",
      10,
    );
    if (attempts >= 5) return;

    checkbox.dataset.qtyBtnAutoClickPending = "true";
    window.sessionStorage.setItem(
      "qty-btn-subscription-auto-click-attempts",
      String(attempts + 1),
    );
    checkbox.click();

    window.setTimeout(() => {
      checkbox.dataset.qtyBtnAutoClickPending = "false";

      const activeCheckbox = document.querySelector(SELECTORS.subscriptionCheckbox);
      const activeCard = activeCheckbox?.closest('[role="checkbox"]');
      if (activeCard?.getAttribute("aria-checked") === "true") {
        window.sessionStorage.setItem("qty-btn-subscription-auto-clicked", "true");
        syncPrices();
        return;
      }

      ensureSubscriptionSelected();
    }, 200);
  }

  function getFormElements() {
    const form = document.querySelector(SELECTORS.productForm);
    if (!form) return {};

    const productForm = form.closest("product-form");
    return {
      form,
      productForm,
      submitButton: form.querySelector(SELECTORS.submitButton),
      errorWrapper: productForm?.querySelector(".product-form__error-message-wrapper"),
      errorMessage: productForm?.querySelector(".product-form__error-message"),
      spinner: productForm?.querySelector(".loading-overlay__spinner"),
    };
  }

  function showError(message) {
    const { errorWrapper, errorMessage } = getFormElements();
    if (!errorWrapper || !errorMessage) return;
    errorMessage.textContent = message;
    errorWrapper.removeAttribute("hidden");
  }

  function clearError() {
    const { errorWrapper } = getFormElements();
    if (errorWrapper) errorWrapper.setAttribute("hidden", "");
  }

  function setLoading(isLoading) {
    const { submitButton, spinner } = getFormElements();
    if (submitButton) {
      submitButton.classList.toggle("loading", isLoading);
      if (isLoading) submitButton.setAttribute("aria-disabled", "true");
      else submitButton.removeAttribute("aria-disabled");
    }
    if (spinner) spinner.classList.toggle("hidden", !isLoading);
  }

  function isQtyBtnPage() {
    return Boolean(document.querySelector(SELECTORS.variantsScript));
  }

  function getSkipCart() {
    const form = document.querySelector(SELECTORS.productForm);
    const variantInput = form?.querySelector('[name="id"]');
    return variantInput?.dataset.skipCart === "true";
  }

  function shouldUseDirectCheckout() {
    return getSkipCart();
  }

  let suppressingCartDrawers = false;

  function endDirectCheckout() {
    document.documentElement.classList.remove("skip-cart-active");
    document.body.classList.remove("skip-cart-active");
  }

  function suppressCartDrawers() {
    if (!document.documentElement.classList.contains("skip-cart-active")) return;
    if (suppressingCartDrawers) return;

    suppressingCartDrawers = true;
    try {
      document.querySelector("cart-drawer")?.close();
      document.body.classList.remove("overflow-hidden");
    } finally {
      suppressingCartDrawers = false;
    }
  }

  function beginDirectCheckout() {
    document.documentElement.classList.add("skip-cart-active");
    document.body.classList.add("skip-cart-active");
    suppressCartDrawers();
  }

  function redirectToCheckout() {
    beginDirectCheckout();
    suppressCartDrawers();
    window.location.replace("/checkout");
  }

  async function clearCart() {
    const response = await fetch("/cart/clear.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
    return response.ok;
  }

  function buildItems() {
    if (isSubscription()) {
      return buildSubscriptionItems();
    }

    return decompose(STATE.quantity).map((item) => ({
      id: item.variant.id,
      quantity: item.qty,
    }));
  }

  function isProductAddToCartTrigger(target) {
    if (!target?.closest) return false;

    return Boolean(
      target.closest(`${SELECTORS.productForm} ${SELECTORS.submitButton}`) ||
        (target.matches?.(SELECTORS.submitButton) && target.closest(SELECTORS.productForm)),
    );
  }

  async function doAddToCart() {
    if (STATE.isProcessing) return;

    if (!STATE.variantsReady) {
      await loadVariants();
      if (!STATE.variantsReady) {
        showError("Product options are still loading. Please try again.");
        return;
      }
    }

    await refreshSellingPlans();

    const items = buildItems();
    if (!items.length) {
      showError("Unable to add this quantity to cart. Please try again.");
      return;
    }

    STATE.isProcessing = true;
    clearError();
    setLoading(true);

    const skipCart = shouldUseDirectCheckout();
    const cartDrawer = document.querySelector("cart-drawer");

    try {
      if (skipCart) {
        beginDirectCheckout();
        await clearCart();
      }

      const body = { items };

      if (!skipCart && cartDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id).join(",");
        body.sections_url = window.location.pathname;
      }

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.status) {
        if (skipCart) endDirectCheckout();
        showError(data.description || data.message || "Error adding to cart.");
        return;
      }

      if (skipCart) {
        redirectToCheckout();
        return;
      }

      if (cartDrawer) {
        cartDrawer.renderContents(data);
      } else {
        window.location = (window.routes && window.routes.cart_url) || "/cart";
      }

      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "qty-btn" });
      }
    } catch (error) {
      if (skipCart) endDirectCheckout();
      console.error("[qty-btn] Add to cart failed:", error);
      showError("Something went wrong. Please try again.");
    } finally {
      STATE.isProcessing = false;
      setLoading(false);

      if (cartDrawer && !skipCart) {
        cartDrawer.classList.remove("is-empty");
      }
    }
  }

  function onAddToCartAttempt(event) {
    if (!isQtyBtnPage()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    doAddToCart();
  }

  function bindCheckoutInterceptor() {
    if (document.documentElement.dataset.qtyBtnCheckoutBound === "true") return;
    if (!isQtyBtnPage()) return;

    document.documentElement.dataset.qtyBtnCheckoutBound = "true";

    document.addEventListener(
      "click",
      (event) => {
        if (!isProductAddToCartTrigger(event.target)) return;
        onAddToCartAttempt(event);
      },
      true,
    );

    document.addEventListener(
      "submit",
      (event) => {
        if (!event.target.matches?.(SELECTORS.productForm)) return;
        onAddToCartAttempt(event);
      },
      true,
    );
  }

  function bindSubmitHook() {
    bindCheckoutInterceptor();
  }

  function bindSubscriptionToggle() {
    if (document.body.dataset.qtyBtnSubscriptionBound === "true") return;
    document.body.dataset.qtyBtnSubscriptionBound = "true";

    document.addEventListener("click", (event) => {
      if (
        event.target.closest(".subscriptionMine.bundleMineData") ||
        event.target.closest(".oneTimeMine.bundleMineData")
      ) {
        runPriceSyncBurst();
      }
    });
  }

  function scheduleBundlesSync() {
    if (bundlesSyncTimer) {
      window.clearTimeout(bundlesSyncTimer);
    }

    bundlesSyncTimer = window.setTimeout(() => {
      ensureControl();
      watchFirstBarPricing();
    }, 16);
  }

  function watchBundles() {
    const bundles = document.querySelector(SELECTORS.bundles);
    if (!bundles) return;

    if (bundlesObserver) {
      bundlesObserver.disconnect();
    }

    bundlesObserver = new MutationObserver(scheduleBundlesSync);
    bundlesObserver.observe(bundles, {
      childList: true,
      subtree: true,
    });
  }

  async function init() {
    loadStoredQuantity();
    STATE.currency = resolveCurrency();
    moneyFormatter = null;

    if (!setupDone) {
      bindSubmitHook();
      bindSubscriptionToggle();
      setupDone = true;
    }

    bootstrapVariantsSync();
    primeInitialPricingMode();

    if (!STATE.variantsReady) {
      await loadVariants();
    } else {
      await refreshVariantPlansFromApi();
      applyEmbeddedSellingPlanGroups();
      enrichVariantsFromKaching();
      buildPackMap();
    }

    ensureControl();
    ensureSubscriptionSelected();
    watchBundles();
    syncPrices();
    syncFrequency();
  }

  injectStyles();
  if (document.querySelector(SELECTORS.variantsScript)) {
    document.documentElement.classList.add("qty-btn-page");
    bindCheckoutInterceptor();
  }
  bootstrapVariantsSync();
  primeInitialPricingMode();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("kaching-bundles-block-loaded", init);
})();
